import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Sparkles } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
const startGoogleLogin = () => {
  const redirect = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
};

const DEMO_EMAIL = "samyram19791975@gmail.com";
const DEMO_PASSWORD = "Sanctum2026!";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");
  const nav = useNavigate();

  // If a valid session (cookie or JWT) is already present, skip the form.
  React.useEffect(() => {
    let cancelled = false;
    api.get("/auth/me")
      .then(() => { if (!cancelled) nav("/admin", { replace: true }); })
      .catch(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Trim to guard against paste-in whitespace
    const cleanEmail = (email || "").trim().toLowerCase();
    const cleanPassword = (password || "").trim();
    if (!cleanEmail || !cleanPassword) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: cleanEmail, password: cleanPassword });
      localStorage.setItem("sanctum_token", data.token);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed. Please try again.";
      setError(detail);
      toast.error(detail);
    } finally { setLoading(false); }
  };

  const fillDemo = () => {
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
    setError("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[--sanctum-bg]">
      {checking ? (
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-2 border-neutral-200 border-t-neutral-900 animate-spin mx-auto mb-4" />
          <p className="font-display text-lg text-neutral-500">Checking your sign-in…</p>
        </div>
      ) : (
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl border border-[--sanctum-border] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.05)]">
        <h1 className="font-display text-3xl mb-1">Admin</h1>
        <p className="text-sm text-neutral-500 mb-6">Sign in to manage the library.</p>

        <button
          type="button"
          data-testid="admin-google-signin"
          onClick={startGoogleLogin}
          className="w-full mb-4 flex items-center gap-3 justify-center py-3 rounded-full bg-white text-neutral-800 text-sm font-medium border border-[--sanctum-border] hover:bg-neutral-50 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.2 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Sign in with Google
        </button>

        <div className="flex items-center gap-3 my-4 text-[10px] tracking-[0.28em] uppercase text-neutral-400">
          <div className="flex-1 h-px bg-[--sanctum-border]" />
          Or with password
          <div className="flex-1 h-px bg-[--sanctum-border]" />
        </div>

        <button
          type="button"
          data-testid="admin-fill-demo"
          onClick={fillDemo}
          className="w-full mb-6 flex items-center gap-2 justify-center py-2.5 rounded-full bg-[--sanctum-secondary] text-neutral-800 text-xs tracking-widest uppercase hover:bg-[--sanctum-secondary]/70 transition-colors"
        >
          <Sparkles size={12} strokeWidth={1.75} className="text-[--sanctum-accent]" />
          Use demo credentials
        </button>

        <div className="space-y-4">
          <div>
            <label className="eyebrow block mb-1.5">Email</label>
            <input
              data-testid="admin-email"
              type="email" required autoComplete="username"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-[--sanctum-border] bg-white focus:outline-none focus:border-neutral-400"
            />
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Password</label>
            <div className="relative">
              <input
                data-testid="admin-password"
                type={showPw ? "text" : "password"}
                required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full h-11 px-3 pr-10 rounded-lg border border-[--sanctum-border] bg-white focus:outline-none focus:border-neutral-400"
              />
              <button
                type="button"
                data-testid="admin-password-toggle"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-neutral-500 hover:text-neutral-900"
              >
                {showPw ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div
            data-testid="admin-login-error"
            className="mt-4 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}

        <button type="submit" data-testid="admin-login-submit" disabled={loading} className="btn-pill btn-primary w-full mt-6">
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <div className="mt-4 text-center">
          <Link
            to="/admin/forgot"
            data-testid="admin-forgot-link"
            className="text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-4"
          >
            Forgot your password?
          </Link>
        </div>

        <p className="text-[11px] text-neutral-400 mt-6 leading-relaxed text-center">
          Trouble signing in? Tap <em>Use demo credentials</em> above.<br />
          Password is case-sensitive.
        </p>
      </form>
      )}
    </div>
  );
}
